import React, { useState, useEffect } from 'react';
import { X, Monitor, Plus, Minus } from 'lucide-react';
import { Button } from '../ui/Button';

interface AddScreensModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (screensToAdd: number) => void;
  client: {
    id: string;
    username: string;
    max_connections: number;
  };
  isLoading?: boolean;
}

const AddScreensModal: React.FC<AddScreensModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  client,
  isLoading = false
}) => {
  const [screensToAdd, setScreensToAdd] = useState(1);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setScreensToAdd(1); // Reset para 1 quando abrir
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      setIsVisible(false);
      setTimeout(onClose, 200);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const maxScreensCanAdd = 3 - client.max_connections;
  const totalCost = screensToAdd * 1; // 1 crédito por tela
  const newTotalScreens = client.max_connections + screensToAdd;

  const handleConfirm = () => {
    if (!isLoading && screensToAdd > 0 && screensToAdd <= maxScreensCanAdd) {
      onConfirm(screensToAdd);
    }
  };

  const incrementScreens = () => {
    if (screensToAdd < maxScreensCanAdd) {
      setScreensToAdd(prev => prev + 1);
    }
  };

  const decrementScreens = () => {
    if (screensToAdd > 1) {
      setScreensToAdd(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 transition-opacity duration-300 ${
            isVisible ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0'
          }`}
        />

        {/* Modal */}
        <div 
          className={`inline-block w-full max-w-md px-6 pt-6 pb-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg border border-blue-200 ${
            isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 rounded-full bg-blue-50 mr-3">
                <Monitor className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Adicionar Telas
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="mt-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Cliente: <span className="font-medium text-gray-900">{client.username}</span>
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-600">Telas Atuais</p>
                  <p className="text-lg font-semibold text-gray-900">{client.max_connections}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-gray-600">Máximo Permitido</p>
                  <p className="text-lg font-semibold text-blue-900">3</p>
                </div>
              </div>
            </div>

            {/* Contador de Telas */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Quantas telas deseja adicionar?
              </label>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={decrementScreens}
                  disabled={screensToAdd <= 1 || isLoading}
                  className="w-10 h-10 p-0"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-gray-900 w-8 text-center">
                    {screensToAdd}
                  </span>
                  <span className="text-sm text-gray-500">
                    tela{screensToAdd !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={incrementScreens}
                  disabled={screensToAdd >= maxScreensCanAdd || isLoading}
                  className="w-10 h-10 p-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Resumo */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Resumo da Operação</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Telas após adição:</span>
                  <span className="font-medium text-gray-900">{newTotalScreens}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Custo total:</span>
                  <span className="font-medium text-blue-900">{totalCost} crédito(s)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Custo por tela:</span>
                  <span className="font-medium text-gray-900">1 crédito</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading || screensToAdd <= 0 || screensToAdd > maxScreensCanAdd}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Adicionando...
                </div>
              ) : (
                `Adicionar ${screensToAdd} Tela${screensToAdd !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddScreensModal;
